import { apiClient } from './apiClient';

export interface DocumentCategory {
  id: string;
  code: string;
  name: string;
  description?: string;
  icon?: string;
  color?: string;
  prefix: string;
  retentionMonths?: number;
  sortOrder: number;
  isActive: boolean;
  /** UUID của thư mục cha. NULL = thư mục gốc. */
  parentId?: string | null;
  documentCount?: number;
}

export interface UserSummary {
  id: string;
  username: string;
  fullName: string;
  email?: string;
}

export interface DepartmentSummary {
  id: string;
  code: string;
  name: string;
}

export interface DocumentVersion {
  id: string;
  versionNumber: number;
  fileName: string;
  fileSize: number;
  fileMime?: string;
  fileHash?: string;
  pageCount?: number;
  changeNote?: string;
  uploadedBy: UserSummary;
  uploadedAt: string;
  signatureRequestId?: string;
  isSignedVersion: boolean;
}

export interface DocumentApproval {
  id: string;
  approver: UserSummary;
  approvalOrder: number;
  status: 'PENDING' | 'APPROVED' | 'REJECTED' | 'SKIPPED';
  comment?: string;
  actedAt?: string;
}

export interface DocumentTaskLink {
  taskId: string;
  taskCode: string;
  taskTitle: string;
  linkType: string;
}

export type DocumentStatus =
  | 'DRAFT'
  | 'PENDING_APPROVAL'
  | 'APPROVED'
  | 'SIGNED'
  | 'REJECTED'
  | 'ARCHIVED';

export interface Document {
  id: string;
  docCode: string;
  title: string;
  description?: string;
  category?: DocumentCategory;
  department?: DepartmentSummary;
  /** V1.0.31: Tất cả khoa được phép xem (gồm primary + additional). */
  sharedWithDepartments?: DepartmentSummary[];
  owner: UserSummary;
  status: DocumentStatus;
  currentVersion: number;
  tags?: string[];
  effectiveFrom?: string;
  effectiveTo?: string;
  issuedDate?: string;
  /** V1.0.65: 4 levels - PUBLIC: tất cả; INTERNAL: theo khoa; SHARED: theo share record; CONFIDENTIAL: chỉ owner+admin */
  confidentiality: 'PUBLIC' | 'INTERNAL' | 'SHARED' | 'CONFIDENTIAL';
  archived: boolean;
  archivedAt?: string;
  createdAt: string;
  updatedAt: string;
  createdBy: UserSummary;
  updatedBy?: UserSummary;
  versions?: DocumentVersion[];
  approvals?: DocumentApproval[];
  linkedTasks?: DocumentTaskLink[];
  approvalWorkflow?: 'SEQUENTIAL' | 'PARALLEL';
}

export interface DocumentListItem {
  id: string;
  docCode: string;
  title: string;
  categoryCode?: string;
  categoryName?: string;
  categoryColor?: string;
  departmentName?: string;
  ownerName: string;
  status: DocumentStatus;
  currentVersion: number;
  confidentiality: string;
  updatedAt: string;
  currentFileSize?: number;
  currentFileMime?: string;
  pendingApprovals?: number;
  hasSignedVersion: boolean;
  archived?: boolean;
}

export interface DocumentListFilter {
  categoryId?: string;
  departmentId?: string;
  ownerId?: string;
  status?: DocumentStatus;
  keyword?: string;
  tag?: string;
  includeArchived?: boolean;
  archivedOnly?: boolean;  // Khi true → CHỈ archived, override includeArchived
  page?: number;
  pageSize?: number;
}

export interface CreateDocumentRequest {
  title: string;
  description?: string;
  categoryId?: string;
  departmentId?: string;
  /** V1.0.31: Danh sách khoa phụ ngoài departmentId. Tất cả khoa trong list sẽ xem được tài liệu này. */
  additionalDepartmentIds?: string[];
  tags?: string[];
  effectiveFrom?: string;
  effectiveTo?: string;
  issuedDate?: string;
  confidentiality?: string;
  linkedTaskIds?: string[];
  importFromAttachmentId?: string;
}

export const documentService = {
  // Categories
  async listCategories(): Promise<DocumentCategory[]> {
    const { data } = await apiClient.get<DocumentCategory[]>('/documents/categories');
    return data;
  },

  async createCategory(payload: {
    code: string;
    name: string;
    description?: string;
    color?: string;
    prefix: string;
    retentionMonths?: number;
    sortOrder?: number;
    parentId?: string;
  }): Promise<DocumentCategory> {
    const { data } = await apiClient.post<DocumentCategory>('/documents/categories', payload);
    return data;
  },

  async updateCategory(id: string, payload: {
    name: string;
    description?: string;
    color?: string;
    retentionMonths?: number;
    sortOrder: number;
    isActive: boolean;
    parentId?: string | null;
  }): Promise<DocumentCategory> {
    const { data } = await apiClient.put<DocumentCategory>(`/documents/categories/${id}`, payload);
    return data;
  },

  async deleteCategory(id: string): Promise<void> {
    await apiClient.delete(`/documents/categories/${id}`);
  },

  async getCategoryPermissions(categoryId: string): Promise<any[]> {
    const { data } = await apiClient.get(`/documents/categories/${categoryId}/permissions`);
    return data;
  },

  async grantCategoryPermission(categoryId: string, payload: {
    userId?: string;
    role?: string;
    permission: 'VIEW' | 'UPLOAD' | 'MANAGE';
  }): Promise<any> {
    const { data } = await apiClient.post(`/documents/categories/${categoryId}/permissions`, payload);
    return data;
  },

  async revokeCategoryPermission(permissionId: string): Promise<void> {
    await apiClient.delete(`/documents/categories/permissions/${permissionId}`);
  },

  // Documents list
  async list(filter: DocumentListFilter = {}): Promise<{
    items: DocumentListItem[]; total: number; page: number; pageSize: number;
  }> {
    const { data } = await apiClient.get('/documents', { params: filter });
    // Backend trả DocumentDto với nested {category, department, owner, ...}.
    // Flatten về DocumentListItem để phù hợp với UI hiện tại.
    const items = (data.items ?? []).map((d: any): DocumentListItem => ({
      id: d.id,
      docCode: d.docCode,
      title: d.title,
      categoryCode: d.category?.code,
      categoryName: d.category?.name,
      categoryColor: d.category?.color,
      departmentName: d.department?.name,
      ownerName: d.owner?.fullName ?? '',
      status: d.status,
      currentVersion: d.currentVersion,
      confidentiality: d.confidentiality,
      updatedAt: d.updatedAt,
      currentFileSize: d.currentFileSize,
      currentFileMime: d.currentFileMime,
      pendingApprovals: d.pendingApprovals,
      hasSignedVersion: !!d.hasSignedVersion,
      archived: d.archived,
    }));
    return {
      items,
      total: data.total ?? items.length,
      page: data.page ?? 1,
      pageSize: data.pageSize ?? items.length,
    };
  },

  async getById(id: string): Promise<Document> {
    const { data } = await apiClient.get<Document>(`/documents/${id}`);
    return data;
  },

  async create(req: CreateDocumentRequest, file?: File): Promise<Document> {
    const formData = new FormData();
    formData.append('metadata', JSON.stringify(req));
    if (file) formData.append('file', file);
    const { data } = await apiClient.post<Document>('/documents', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return data;
  },

  async update(id: string, req: Partial<CreateDocumentRequest>): Promise<Document> {
    const { data } = await apiClient.patch<Document>(`/documents/${id}`, req);
    return data;
  },

  async archive(id: string): Promise<void> {
    await apiClient.post(`/documents/${id}/archive`);
  },

  async unarchive(id: string): Promise<void> {
    await apiClient.post(`/documents/${id}/unarchive`);
  },

  async delete(id: string): Promise<void> {
    await apiClient.delete(`/documents/${id}`);
  },

  // Versions
  async uploadVersion(id: string, file: File, changeNote?: string): Promise<DocumentVersion> {
    const formData = new FormData();
    formData.append('file', file);
    if (changeNote) formData.append('changeNote', changeNote);
    const { data } = await apiClient.post<DocumentVersion>(`/documents/${id}/versions`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return data;
  },

  async download(id: string): Promise<Blob> {
    const { data } = await apiClient.get(`/documents/${id}/download`, { responseType: 'blob' });
    return data;
  },

  async downloadVersion(id: string, versionNumber: number): Promise<Blob> {
    const { data } = await apiClient.get(`/documents/${id}/versions/${versionNumber}/download`, {
      responseType: 'blob',
    });
    return data;
  },

  getPreviewUrl(id: string): string {
    const base = (import.meta.env.VITE_API_BASE_URL || '/api').replace(/\/$/, '');
    return `${base}/documents/${id}/preview`;
  },

  // Approvals
  async submitForApproval(id: string, approverIds: string[], workflow: 'SEQUENTIAL' | 'PARALLEL', comment?: string): Promise<void> {
    await apiClient.post(`/documents/${id}/submit-approval`, { approverIds, workflow, comment });
  },

  async approve(id: string, comment?: string): Promise<void> {
    await apiClient.post(`/documents/${id}/approve`, { comment });
  },

  async reject(id: string, comment?: string): Promise<void> {
    await apiClient.post(`/documents/${id}/reject`, { comment });
  },

  // Task linking
  async linkTask(id: string, taskId: string, linkType: string = 'RELATED'): Promise<void> {
    await apiClient.post(`/documents/${id}/link-task`, { taskId, linkType });
  },

  async unlinkTask(id: string, taskId: string): Promise<void> {
    await apiClient.delete(`/documents/${id}/link-task/${taskId}`);
  },

  // ============================================================
  // V1.0.65: Document Shares + Access Log
  // ============================================================

  async listShares(documentId: string): Promise<DocumentShare[]> {
    const { data } = await apiClient.get<DocumentShare[]>(`/documents/${documentId}/shares`);
    return data;
  },

  async createShares(documentId: string, req: CreateShareRequest): Promise<DocumentShare[]> {
    const { data } = await apiClient.post<DocumentShare[]>(`/documents/${documentId}/shares`, req);
    return data;
  },

  async updateShare(shareId: string, req: UpdateShareRequest): Promise<DocumentShare> {
    const { data } = await apiClient.patch<DocumentShare>(`/documents/shares/${shareId}`, req);
    return data;
  },

  async revokeShare(shareId: string): Promise<void> {
    await apiClient.delete(`/documents/shares/${shareId}`);
  },

  async listSharedWithMe(filter?: { page?: number; pageSize?: number; keyword?: string }): Promise<{ items: Document[]; total: number; page: number; pageSize: number }> {
    const params: any = {};
    if (filter?.page) params.Page = filter.page;
    if (filter?.pageSize) params.PageSize = filter.pageSize;
    if (filter?.keyword) params.Keyword = filter.keyword;
    const { data } = await apiClient.get('/documents/shared-with-me', { params });
    return data;
  },

  async getAccessLog(documentId: string, page = 1, pageSize = 50): Promise<{ items: DocumentAccessLog[]; total: number; page: number; pageSize: number }> {
    const { data } = await apiClient.get(`/documents/${documentId}/access-log`, { params: { page, pageSize } });
    return data;
  },
};

// ============================================================
// V1.0.65: Type definitions
// ============================================================

export interface DocumentShare {
  id: string;
  documentId: string;
  sharedWithUser: { id: string; fullName: string; avatarUrl: string | null; position: string | null };
  sharedByUser: { id: string; fullName: string; avatarUrl: string | null; position: string | null };
  sharedAt: string;
  expiresAt: string | null;
  canDownload: boolean;
  note: string | null;
  isActive: boolean;
}

export interface CreateShareRequest {
  userIds: string[];
  expiresAt?: string | null;
  canDownload?: boolean;
  note?: string | null;
}

export interface UpdateShareRequest {
  expiresAt?: string | null;
  canDownload?: boolean;
  note?: string | null;
}

export interface DocumentAccessLog {
  id: number;
  documentId: string;
  userId: string | null;
  username: string | null;
  action: 'VIEW' | 'DOWNLOAD' | 'SHARE' | 'UPDATE_SHARE' | 'UNSHARE' | 'CHANGE_VISIBILITY' | 'CREATE' | 'UPDATE' | 'DELETE' | string;
  detail: string | null;
  ip: string | null;
  createdAt: string;
}
